//#region Import
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Router, Route, Switch, Redirect } from 'react-router-dom';

import history from './services/history';
import { logPageView, logEvent } from './services/ga';

import { actions as appActions, selectors as appSelectors } from './modules/appModule';
import { actions as userActions } from './modules/userModule';
import { actions as enumsActions } from './modules/enumsModule';
import { actions as synopsisActions } from './modules/synopsisModule';
import { BASE, PATH } from './modules/constants'

import { useIdleTimer } from "react-idle-timer";

//#region Pages
import ConfirmAccount from './pages/auth/ConfirmAccount';
import DeleteAccountConfirm from './pages/auth/DeleteAccountConfirm';
import DeleteGoodbye from './pages/auth/DeleteGoodbye';
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import MyAccount from './pages/account/MyAccount';
import PersonSearchPage from './pages/search/PersonSearchPage';
import AiCostsPage from './pages/monitoring/AiCostsPage'
import AlertsConfig from './pages/monitoring/AlertsConfig';
import MonitoringConfigPage from './pages/monitoring/MonitoringConfigPage'
import NexusPage from './pages/nexus/NexusPage'
import AdviserPage from './pages/nexus/AdviserPage'
import PrivacyPolicy from './pages/info/PrivacyPolicy'
import HowToBookmark from './pages/info/HowToBookmark'
import AboutNumerable from './pages/info/AboutNumerable'
import Register from './pages/auth/Register'
import HomePage from './pages/home/HomePage'
import SetNewPassword from './pages/auth/SetNewPassword'
import Splash from './pages/info/Splash'
import TermsAndConditions from './pages/info/TermsAndConditions'
import Status from './pages/info/StatusPage'
//#endregion Pages

import { ThemeProvider } from '@mui/material/styles'
import { theme } from './services/theme'
import NewsRelease from './pages/news/NewsReleasePage'
//#endregion Import

const IDLE_TIMEOUT_HOURS = 48 // 2 days
const IDLE_TIMEOUT_MINUTES = IDLE_TIMEOUT_HOURS * 60
const IDLE_TIMEOUT_SECONDS = IDLE_TIMEOUT_MINUTES * 60

history.listen((location, action) => {
  logPageView(location);
  logEvent(location);
})

logPageView(history.location);
logEvent(history.location);

class SwitchWithLoad extends Component {
  componentDidMount() {
    this.props.onLoad();
  }

  render() {
    return <Switch>{this.props.children}</Switch>;
  }
}

class App extends Component {

  onSizeChange = () => this.props.user.changeSize()

  componentDidMount() {
    this.onSizeChange()
    window.addEventListener('resize', this.onSizeChange)
    this.props.app.start()
    this.props.enums.get()
  }

  render() {
    // console.log('app this.props:',this.props)

    const { loggedIn, loaded } = this.props;

    // These are named components which only render a Switch in a certain state
    const Loading = ({ children }) => (!loaded ? <Switch>{children}</Switch> : null);

    // LoggedIn to Standard
    const NotLoggedIn = ({ children }) => ((loaded && !loggedIn) ? <Switch>{children}</Switch> : null);

    // Fetches user.load() and the DashboardNavbar Status badge's data (synopsis.get) once, right
    // after login, regardless of which page is landed on first (direct URL, bookmark, or a hard
    // reload) - otherwise a page that doesn't separately fetch synopsis data itself (most pages;
    // only StatusPage and, for Developer users, a HomePage-scheduled job do) leaves the Status
    // badge on its default/unloaded colour (orange) even though the real status is fine.
    const LoggedIn = ({ children }) => (loaded && loggedIn ? <SwitchWithLoad onLoad={() => { this.props.user.load(); this.props.synopsis.get() }}>{children}</SwitchWithLoad> : null);

    const homePagePath = `/(` +
      `${PATH[BASE.FAVOURITES]}|` +
      `${PATH[BASE.LSE.ROLES]}|` +
      `${PATH[BASE.LSE350]}|` + // legacy, to redirect on 350 Club
      `${PATH[BASE.LSE.COMPANIES]}|` +
      `${PATH[BASE.LSE.NEWS]}|` +
      `${PATH[BASE.LSE.PEOPLE]}|` +
      `${PATH[BASE.LSE.ADVISERS]})` +
      `${PATH[BASE.SEARCH]})` +
      `${PATH[BASE.DASHBOARD]})`

    // Because App is a component class we can’t call hooks directly in it.
    // So, we wrap useIdleTimer in a small wrapper functional component that uses useIdleTimer and calls back into the class
    const IdleWatcher = ({ onIdle, onActive, timeout }) => {
      useIdleTimer({ timeout, onIdle, onActive })
      return null // it doesn’t render anything
    }

    // zoom if on 350 Club in portrait, as iframe does not know its on mobile!
    // const zoomFactor =this.props.the350club && this.props.portrait ? 1.0 : 1.0

    return (
      <ThemeProvider theme={theme}>
        <Router history={history} >
          <div id="App" className="App" > {/* style={{zoom:zoomFactor}}> */}

            <IdleWatcher
              timeout={IDLE_TIMEOUT_SECONDS * 1000}
              onIdle={() => this.props.app.activityIdle()}
              onActive={() => this.props.app.activityActive()}
            />

            <Loading>
              <Route path="*" component={Splash} />
            </Loading>

            <NotLoggedIn>
              {/* Account */}
              <Route path={`/${PATH.LOGIN}`} component={Login} />
              <Route path={`/${PATH.CONFIRM}`} component={ConfirmAccount} />
              <Route path={`/${PATH.GOODBYE}`} component={DeleteGoodbye} />
              <Route path={`/${PATH.FORGOT_PASSWORD}`} component={ForgotPassword} />
              <Route path={`/${PATH.REGISTER}`} component={Register} />
              <Route path={`/${PATH.SET_NEW_PASSWORD}`} component={SetNewPassword} />

              {/* Companies House */}
              <Route path={`/${PATH[BASE.PERSON]}/:personId`} component={NexusPage} />
              <Route path={`/${PATH[BASE.COMPANY]}/:companyId`} component={NexusPage} />
              <Route path={`/${PATH[BASE.ADVISER]}/:adviserId`} component={AdviserPage} />
              <Route path={`/${PATH[BASE.SEARCH]}`} component={HomePage} />

              {/* Menu */}
              <Route path={`/${PATH.T_AND_C}`} component={TermsAndConditions} />
              <Route path={`/${PATH.PRIVACY}`} component={PrivacyPolicy} />
              <Route path={`/${PATH.BOOKMARKING}`} component={HowToBookmark} />
              <Route path={`/${PATH.ABOUT}`} component={AboutNumerable} />

              {/* Other */}
              <Route path={`/(${PATH.PSEARCH}|${PATH.CSEARCH})`} component={PersonSearchPage} />
              <Route path={`/${PATH[BASE.NEWS_RELEASE]}/:newsId`} component={NewsRelease} />

              {/* catchall */}
              <Route path="/" component={HomePage} />
              <Redirect from="*" to="/" />
            </NotLoggedIn>

            <LoggedIn>
              {/* Account */}
              <Route path={`/${PATH.MY_ACCOUNT}`} component={MyAccount} />
              <Route path={`/${PATH.DELETE_ACCOUNT}`} component={DeleteAccountConfirm} />
              <Route path={`/${PATH.SET_NEW_PASSWORD}`} component={SetNewPassword} />

              {/* Home page */}
              <Route path={homePagePath} component={HomePage} />

              {/* Nexus page*/}
              <Route path={`/${PATH[BASE.PERSON]}/:personId`} component={NexusPage} />
              <Route path={`/${PATH[BASE.COMPANY]}/:companyId`} component={NexusPage} />
              <Route path={`/${PATH[BASE.ADVISER]}/:adviserId`} component={AdviserPage} />
              {/* Menu */}
              <Route path={`/${PATH.T_AND_C}`} component={TermsAndConditions} />
              <Route path={`/${PATH.PRIVACY}`} component={PrivacyPolicy} />
              <Route path={`/${PATH.BOOKMARKING}`} component={HowToBookmark} />
              <Route path={`/${PATH.ABOUT}`} component={AboutNumerable} />
              <Route path={`/${PATH.USER_ALERTS_CONFIG}`} component={AlertsConfig} />
              <Route path={`/${PATH.MONITORING_CONFIG}`} component={MonitoringConfigPage} />
              <Route path={`/${PATH.AI_COSTS}`} component={AiCostsPage} />

              {/* Other */}
              <Route path={`/(${PATH.PSEARCH}|${PATH.CSEARCH})`} component={PersonSearchPage} />
              <Route path={`/${PATH[BASE.NEWS_RELEASE]}/:newsId`} component={NewsRelease} />

              <Route path={`/${PATH[BASE.STATUS]}`} component={Status} />

              {/* catchall */}
              <Route path="/" component={HomePage} />
              <Redirect from="*" to="/" />
            </LoggedIn>
          </div>
        </Router>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = state => {
  return {
    urlSource: state.common.urlSource,
    loaded: appSelectors.isLoaded(state.app),
    loggedIn: appSelectors.isLoggedIn(state.app),

    the350club: state.common.the350club,
    portrait: state.user.portrait,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    app: bindActionCreators(appActions, dispatch),
    user: bindActionCreators(userActions, dispatch),
    enums: bindActionCreators(enumsActions, dispatch),
    synopsis: bindActionCreators(synopsisActions, dispatch),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(App);
